import { useState } from 'react'
import { useTranslation } from '../../i18n/LanguageContext'
import { useCutlery, type CutleryType, type CutleryItem } from './useCutlery'
import { useTimer } from '../useTimer'
import { CutleryIcon } from './CutlerySVGs'
import { s } from './cutleryStyles'
import GamePlayHeader from '../GamePlayHeader'

const SLOT_COLORS: Record<CutleryType, { liner: string; border: string; accent: string }> = {
  spoon:    { liner: '#FFF8EE', border: '#E8C87A', accent: '#D4A017' },
  fork:     { liner: '#EDF6EE', border: '#8FC89A', accent: '#52B788' },
  knife:    { liner: '#F5F0EA', border: '#C0A882', accent: '#8B6347' },
  teaspoon: { liner: '#EEF3FF', border: '#94A8D8', accent: '#4A6FA5' },
}

/* ── Drawer Play Screen ───────────────────────────────────────── */
export function DrawerPlayScreen({ state, onSelectItem, onPlaceInSlot, onPlaceItemById, onBack }: {
  state:           ReturnType<typeof useCutlery>['state']
  onSelectItem:    (id: string) => void
  onPlaceInSlot:   (type: CutleryType) => void
  onPlaceItemById: (id: string, type: CutleryType) => void
  onBack:          () => void
}) {
  const { t }       = useTranslation()
  const c           = t.cutlery
  const elapsed     = useTimer(state.startTime, state.endTime)
  const sorted      = state.total - state.pile.length
  const hasSelected = state.selectedId !== null

  return (
    <div style={s.playRoot}>
      <GamePlayHeader
        title={c.title}
        stats={[
          { value: state.moves, label: c.moves },
          { value: elapsed,     label: c.time },
        ]}
        progress={(sorted / state.total) * 100}
        progressNow={sorted}
        progressMax={state.total}
        onBack={onBack}
        backLabel={c.backToDashboard}
      />

      <main style={s.playMain}>
        <div style={s.drawerFrame}>
          <div style={s.drawerInner}>
            {state.types.map((type) => (
              <Compartment
                key={type}
                type={type}
                label={c.slots[type]}
                hints={state.hints[type]}
                sorted={state.drawer[type]}
                isWrong={state.wrongSlot === type}
                hasSelection={hasSelected}
                onClick={() => hasSelected && onPlaceInSlot(type)}
                onDrop={(id) => onPlaceItemById(id, type)}
              />
            ))}
          </div>
        </div>

        <p style={s.instruction}>
          {hasSelected ? c.instructionPlace : c.instructionPick}
        </p>

        <div style={s.pile}>
          {state.pile.map((item) => (
            <PileCard
              key={item.id}
              item={item}
              isSelected={state.selectedId === item.id}
              dimmed={hasSelected && state.selectedId !== item.id}
              onClick={() => onSelectItem(item.id)}
            />
          ))}
        </div>
      </main>
    </div>
  )
}

/* ── Compartment ──────────────────────────────────────────────── */
function Compartment({ type, label, hints, sorted, isWrong, hasSelection, onClick, onDrop }: {
  type:         CutleryType
  label:        string
  hints:        CutleryItem[]
  sorted:       CutleryItem[]
  isWrong:      boolean
  hasSelection: boolean
  onClick:      () => void
  onDrop:       (itemId: string) => void
}) {
  const colors     = SLOT_COLORS[type]
  const [dragOver, setDragOver] = useState(false)

  // Hints disappear once the first real item is sorted here
  const visibleHints = sorted.length === 0 ? hints : []
  const allItems     = [...visibleHints, ...sorted]
  const visible      = allItems.slice(0, 6)
  const overflow     = allItems.length - visible.length

  const isHighlighted = dragOver || (hasSelection && !dragOver)
  const borderColor   = isWrong ? '#B5541A' : dragOver ? colors.accent : isHighlighted ? colors.accent : colors.border
  const glow          = dragOver
    ? `0 0 0 4px ${colors.accent}60, inset 0 2px 8px rgba(0,0,0,0.06)`
    : isHighlighted
      ? `0 0 0 3px ${colors.accent}40, inset 0 2px 8px rgba(0,0,0,0.06)`
      : 'inset 0 2px 8px rgba(0,0,0,0.06)'

  return (
    <div
      className={isWrong ? 'wm-shake' : ''}
      onClick={onClick}
      onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOver(true) }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault()
        setDragOver(false)
        const id = e.dataTransfer.getData('text/plain')
        if (id) onDrop(id)
      }}
      role="button"
      aria-label={label}
      style={{
        ...s.compartment,
        background:  dragOver ? `color-mix(in srgb, ${colors.liner} 85%, ${colors.accent} 15%)` : colors.liner,
        border:      `2px solid ${borderColor}`,
        boxShadow:   glow,
        cursor:      hasSelection ? 'pointer' : 'default',
        transform:   dragOver ? 'scale(1.02)' : 'scale(1)',
        transition:  'border-color 0.15s ease, box-shadow 0.2s ease, transform 0.12s ease, background 0.15s ease',
      }}
    >
      <div style={s.compartmentPieces}>
        {visible.map((item, i) => {
          const isHint = i < visibleHints.length
          return (
            <div
              key={item.id}
              style={{
                ...s.compartmentPiece,
                opacity: isHint ? 0.5 : 1,
                outline: isHint ? `1.5px dashed ${colors.accent}` : 'none',
                outlineOffset: 2,
              }}
            >
              <CutleryIcon type={item.type} width={16} />
            </div>
          )
        })}
        {overflow > 0 && (
          <span style={{ ...s.compartmentOverflow, color: colors.accent }}>+{overflow}</span>
        )}
      </div>

      <div style={s.compartmentFooter}>
        <span style={{ ...s.compartmentLabel, color: colors.accent }}>{label}</span>
      </div>
    </div>
  )
}

/* ── Pile Card ────────────────────────────────────────────────── */
export function PileCard({ item, isSelected, dimmed, onClick }: {
  item:       { id: string; type: CutleryType }
  isSelected: boolean
  dimmed:     boolean
  onClick:    () => void
}) {
  const [dragging, setDragging] = useState(false)

  return (
    <button
      draggable
      onClick={onClick}
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', item.id)
        e.dataTransfer.effectAllowed = 'move'
        setDragging(true)
      }}
      onDragEnd={() => setDragging(false)}
      style={{
        ...s.pileCard,
        ...(isSelected ? s.pileCardSelected : {}),
        opacity:   dragging ? 0.35 : dimmed ? 0.45 : 1,
        transform: isSelected ? 'translateY(-8px) scale(1.08)' : 'translateY(0) scale(1)',
        cursor:    dragging ? 'grabbing' : 'grab',
      }}
      aria-pressed={isSelected}
    >
      <CutleryIcon type={item.type} width={28} />
    </button>
  )
}
